import { useEffect, useRef } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";
import { Event } from "../assets/interfaces/interfaces";
import { NEWS_LOGOS, NEWS_SOURCES } from "../utils/newsLogos";

dayjs.extend(utc);
dayjs.extend(duration);

export const findLongestName = (user1, user2 = {}) => {
  const possibleNames = [];
  const addUserNames = (user) => {
    if (!!user?.firstName && !!user?.lastName) {
      possibleNames.push(`${user.firstName} ${user.lastName}`);
    }
    // users who sso with apple have this field instead of first and last names
    if (user?.name) {
      possibleNames.push(user?.name);
    }
    if (user?.display_name) {
      possibleNames.push(user?.display_name);
    }
    if (user?.displayName) {
      possibleNames.push(user?.displayName);
    }
  };

  addUserNames(user1);
  addUserNames(user2);

  if (possibleNames.length === 0) return "";

  // Find the longest name
  const longestName = possibleNames.reduce((longest, current) => {
    return current.length > longest.length ? current : longest;
  });

  return longestName;
};

export const transformArray = (array, user, setInvalidChat: ((bool?: boolean) => void) = () => { }) => {
  const newArray = [];
  const sortedArray = [...array].sort((a, b) => a.id - b.id);
  sortedArray.forEach((item) => {
    const chatUserId = item.user_id;
    const signedInUserId = user?.userId || "";
    if (chatUserId === signedInUserId) {
      // Create an object for the user query
      newArray.push({
        id: item.id,
        isBot: false,
        output: item.user_query,
        title: item.session_title,
        date: item.created_at,
        chat_id: `prompt-${item.message_id}`,
        attachments: item?.bot_response_payload.attachments || null,
      });

      // Create an object for the bot response
      newArray.push({
        id: item.id,
        date: item.created_at,
        isBot: true,
        outputLoading: false,
        message_id: item.message_id,
        user_query: item.user_query,
        output: item.bot_response,
        chart_data: item?.bot_response_payload?.chart_data, // Assuming these values are default
        chat_id: item.message_id, // Assuming message_id is the chat_id
        chart_flag: JSON.parse(item.chart_output),
        title: item.session_title,
        list_of_tickers: item?.bot_response_payload?.list_of_tickers,
        news_summary: item?.bot_response_payload?.news_summary,
        preferences: item?.preferences,
      });
    }
    else {
      // chat does not belong to this user id
      setInvalidChat(true);
    }
  });
  return newArray;
};
export const shareTransform = (array) => {
  const newArray = [];
  const sortedArray = [...array].sort((a, b) => a.id - b.id);
  sortedArray.forEach((item) => {
    // Create an object for the user query
    newArray.push({
      id: item.id,
      isBot: false,
      output: item.user_query,
      title: item.session_title,
      date: item.created_at,
      chat_id: `prompt-${item.message_id}`,
      attachments: item?.bot_response_payload.attachments || null,
    });

    // Create an object for the bot response
    newArray.push({
      id: item.id,
      date: item.created_at,
      isBot: true,
      outputLoading: false,
      output: item.bot_response,
      message_id: item.message_id,
      chart_data: item?.bot_response_payload?.chart_data, // Assuming these values are default
      chat_id: item.message_id, // Assuming message_id is the chat_id
      chart_flag: JSON.parse(item.chart_output),
      title: item.session_title,
      list_of_tickers: item?.bot_response_payload?.list_of_tickers,
      news_summary: item?.bot_response_payload?.news_summary,
      preferences: item?.preferences,
    });
  });
  return newArray;
};

// receive an array of chat history objects
export const groupChatsByDate = (pages, limit = 0) => {
  const now = dayjs();

  const grouped = {
    today: [],
    yesterday: [],
    last7Days: [],
    last30Days: [],
    older: [],
  };

  let processed = 0;

  outer: for (const page of pages || []) {
    for (const chat of page.records || []) {
      const createdAt = dayjs(chat.date);

      if (createdAt.isSame(now, "day")) {
        grouped.today.push(chat);
      }
      else if (createdAt.isSame(now.subtract(1, "day"), "day")) {
        grouped.yesterday.push(chat);
      }
      else if (createdAt.isAfter(now.subtract(7, "day"))) {
        grouped.last7Days.push(chat);
      }
      else if (createdAt.isAfter(now.subtract(30, "day"))) {
        grouped.last30Days.push(chat);
      }
      else {
        grouped.older.push(chat);
      }

      processed += 1;
      if (limit > 0 && processed >= limit) break outer;
    }
  }

  return grouped;
};

export const formatGroupName = (key: string) => {
  switch (key) {
    case "today": return "Today";
    case "yesterday": return "Yesterday";
    case "last7Days": return "Previous 7 Days";
    case "last30Days": return "Previous 30 Days";
    case "older": return "Over 30 Days Ago";
    default: return key;
  }
};

export const themesTV = {
  light: {
    layout: {
      background: { color: "#FFFFFF" }, // A soft light background that's easy on the eyes
      textColor: "rgba(28, 30, 34, 0.9)", // Dark text for high contrast on light background
    },
    grid: {
      vertLines: {
        color: "#ECEFF1", // Light grey for vertical grid lines, subtle and non-distracting
        style: 2, // Dashed lines
      },
      horzLines: {
        color: "#ECEFF1", // Matching horizontal lines with vertical ones
        style: 2, // Dashed lines
      },
    },
    timeScale: {
      borderColor: "#D1D4DC", // A border color that blends well with the layout
      timeVisible: true, // Ensure time is visible
      secondsVisible: false, // Avoid clutter by hiding seconds
    },
    priceScale: {
      borderColor: "#D1D4DC", // Matching the time scale border
      autoScale: true,
    },
    crosshair: {
      mode: 1, // Show crosshair by default
      vertLine: {
        color: "#758696", // A color that stands out but isn't too bold
        style: 0, // Solid line
        width: "1px",
        labelVisible: true,
      },
      horzLine: {
        color: "#758696", // Matching vertical crosshair
        style: 0, // Solid line
        width: "1px",
        labelVisible: true,
      },
    },
  },
  dark: {
    layout: {
      background: { color: "#1E1E1E" }, // A deep dark background for night mode
      textColor: "rgba(216, 222, 233, 0.9)", // Light text for contrast
    },
    grid: {
      vertLines: {
        color: "#2B2B43", // Darker lines for a subtle grid
        style: 2, // Dashed lines for texture
      },
      horzLines: {
        color: "#2B2B43", // Consistent with vertical lines
        style: 2, // Dashed lines to match
      },
    },
    timeScale: {
      borderColor: "#262B41", // A darker border for the timescale, blending with the background
      timeVisible: true, // Ensure time is visible
      secondsVisible: false, // Keep the interface clean
    },
    priceScale: {
      borderColor: "#262B41", // Matching the time scale border for consistency
      autoScale: true,
    },
    crosshair: {
      mode: 1, // Default crosshair visibility
      vertLine: {
        color: "#88CFFA", // A bright blue for visibility against the dark background
        style: 0, // Solid line for clarity
        width: 1,
        labelVisible: true,
      },
      horzLine: {
        color: "#88CFFA", // Matching the vertical line for consistency
        style: 0, // Solid line
        width: 1,
        labelVisible: true,
      },
    },
  },
};

export function processDataForTradingView(ohlcvData: unknown[]) {
  // Convert the data for the candlestick series
  const candlestickData = ohlcvData.map(([time, open, high, low, close]) => ({
    time: time / 1000, // Convert milliseconds to seconds if necessary
    open,
    high,
    low,
    close,
  }));

  // Convert the data for the volume series
  const volumeData = ohlcvData.map(([time, , , , , volume]) => ({
    time: time / 1000, // Convert milliseconds to seconds if necessary
    value: volume,
  }));

  return { candlestickData, volumeData };
}
export const processData = (data: number[][]) => {
  const firstDataPoint = data[0][1]; // Assuming the first element is the starting point
  return data.map(point => [
    point[0], // Keep the datetime value unchanged
    ((point[1] - firstDataPoint) / firstDataPoint) * 100, // Convert to percentage change
  ]);
};

export function useDebounce(value, setter, delay) {
  const previousValueRef = useRef(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (value !== previousValueRef.current) {
        setter(value);
        previousValueRef.current = value;
      }
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay, setter]);
}
// formatting - using abbreviations for large numbers and rounding to 2 dp to avoid long dp
export const formatNumericValue = (value: number): string => {
  if (value >= 1_000_000_000_000) {
    return `${(value / 1_000_000_000_000).toFixed(2)}T`;
  }
  else if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(2)}B`;
  }
  else if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(2)}M`;
  }
  else {
    const decimalPart = value.toString().split(".")[1];
    if (decimalPart && decimalPart.length >= 1) {
      return value.toFixed(2);
    }
  }
  return value.toString();
};

export const parseAnalyticsParams = (params) => {
  const result: Record<string, string> = {};
  for (const key in params) {
    switch (key) {
      case "session_id":
        result.sessionId = params[key];
        break;
      case "user_id":
        result.userId = params[key];
        break;
      default:
        result[key] = params[key];
        break;
    }
  }
  return result;
};

export const getDynamicPathObject = (path, params) => {
  let dynamicPath = path;
  for (const key in params) {
    const value = params[key];
    dynamicPath = dynamicPath.replace(value, `:${key}`);
  }
  return dynamicPath;
};

export const numberFormatter = (number, decimalPlaces = 2) => {
  if (isNaN(number)) return "";

  const formatter = customFormatter(decimalPlaces);
  let formattedNumber = formatter.format(number);

  if (decimalPlaces > 2) {
    formattedNumber = formattedNumber.replace(/(\.\d*?)0+$/, "$1");
    if (formattedNumber.endsWith(".")) {
      formattedNumber = formattedNumber.slice(0, -1);
    }
  }

  return formattedNumber;
};

export const currencyFormatter = (number, decimalPlace = 2, isCurrency = true) => {
  if (isNaN(number)) return "";

  const formatter = customFormatter(decimalPlace, isCurrency);
  const formattedNumber = formatter.format(number);

  return formattedNumber;
};

const customFormatter = (decimalPlaces = 2, withCurrency = false) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    ...(
      withCurrency
        ? {
            style: "currency",
            currency: "USD",
          }
        : {}
    ),
  });
};

export const processSparklineData = (data, setSeries) => {
  if (data?.results) {
    const seriesForSparkline = data.results
      .slice(0, 16)
      .map(result => ({
        price: result.c, // closing price
        time: result.t, // timestamp
      }))
      .reverse();

    if (Array.isArray(seriesForSparkline) && seriesForSparkline.length >= 16) {
      setSeries(seriesForSparkline);
    }
    else {
      setSeries("Data unavailable");
    }
  }
  else {
    setSeries("Data unavailable");
  }
};

export const isWithinLastDay = (timestamp: string) => {
  return dayjs(timestamp).isAfter(dayjs().subtract(1, "day"));
};

export const daysSince = (timestamp: string): number => {
  const givenDate = dayjs.utc(timestamp);
  const today = dayjs.utc();

  return Math.ceil(today.diff(givenDate, "day", true));
};

export const getSourceLogo = (newsUrl: string): string | null => {
  try {
    const domain = getDomain(newsUrl);
    return domain ? NEWS_LOGOS[domain] || domain : null;
  }
  catch {
    return null;
  }
};

export const getSourceLogos = (events: Event[]) => {
  const logos = new Set<string>();
  events.forEach((event) => {
    const logo = getSourceLogo(event.news_url);
    if (logo) logos.add(logo);
  });
  return Array.from(logos);
};

export const formatTimestamp = (timestamp: string | number): string => {
  return dayjs(timestamp).format("MMM D, YYYY [at] HH:mm");
};

export const formatTimestampToDate = (timestamp: string | number): string => {
  return dayjs(timestamp).format("MMM D, YYYY");
};

export const convertCurrentTimestamp = (): string => {
  return dayjs().toISOString();
};

export const getDomain = (url: string): string | null => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  }
  catch {
    return null;
  }
};

export const getSourceName = (newsUrl: string): string | null => {
  const domain = getDomain(newsUrl);
  return domain ? NEWS_SOURCES[domain] || domain : null;
};

export const formattedNotifMessage = (message: string) => {
  return message
    .replace(/^.*\//, "")
    .replaceAll("-", " ")
    .replace(/^([a-z])/, match => match.toUpperCase());
};

export const capitalizeFirstChar = text =>
  text.replace(/\b\w/g, char => char.toUpperCase());

export const areArraysEqual = (arr1, arr2) =>
  JSON.stringify([...arr1].sort()) === JSON.stringify([...arr2].sort());

export const getChipWidth = (label: string) => {
  const span = document.createElement("span");
  span.style.visibility = "hidden";
  span.style.position = "absolute";
  span.style.whiteSpace = "nowrap";
  span.textContent = label;
  document.body.appendChild(span);
  const width = span.offsetWidth + 8;
  document.body.removeChild(span);
  return width;
};

export const isValidSearchText = (text) => {
  const regexForAlphanumericChar = /[a-zA-Z0-9]/;
  return regexForAlphanumericChar.test(text);
};

// covert date to 'LL' format May 22, 2025
export const formatDate = (date?: string) => {
  if (!date) return "";
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : "";
};
export const formatSeconds = (seconds: number) => {
  return dayjs.duration(seconds, "seconds").format("m:ss");
};

export const validateSearchInput = (input: string) => {
  if (!isValidSearchText(input)) {
    return "SPECIAL_CHARACTERS";
  }
  if (input.trim().length < 3) {
    return "INSUFFICIENT_CHARACTERS";
  }
  if (input.length > 75) {
    return "EXCEED_CHARACTER_COUNT";
  }
  return null;
};

export const sanitizeInput = (input: string): string => {
  if (!input || typeof input !== "string") {
    return null;
  }
  const sanitized = input
    .trim()
    // Remove only the most dangerous patterns
    .replace(/<script[^>]*>.*?<\/script>/gi, "") // script tag removal
    .replace(/javascript:/gi, "") // Remove javascript: protocol
    .replace(/vbscript:/gi, "") // Remove vbscript: protocol
    .replace(/onload|onerror|onclick/gi, "") // Remove event handlers
    .replace(/\s+/g, " ") // Normalize spaces
    .trim();

  if (!sanitized || sanitized.length === 0) {
    return null;
  }

  return sanitized;
};
